import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BulkLogsViewFilterType } from '@common-reducers/CampaignReducer';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { CAMPAIGN_STATUS_COLORS } from '@common/services/campaign-status-colors';

interface MessageStats {
    sent: number;
    confirmed?: number; // Reached WhatsApp's servers, no delivery receipt yet (split out of `sent`)
    delivered: number;
    read: number;
    played: number;
    failed: number;
    pending: number;
    total: number;
    expired?: number; // Messages with status "bt-sent" but past retry limit
}

interface CampaignMessageLogStatusFilterProps {
    messageStats: MessageStats;
    activeFilter: BulkLogsViewFilterType;
    onFilterClick: (filter: BulkLogsViewFilterType) => void;
    /** BE#710 — count of "not on WhatsApp" failures (from the server-side breakdown).
     *  Renders an extra filter chip when > 0. */
    notOnWhatsAppCount?: number;
}

// A single clickable stat — a slim pill (colour dot · number · label), matching the
// campaign detail page's delivery legend rather than the old bulky icon cards. Active
// = a tinted fill + a 1px ring in the status colour. Styled with the campaigns-rd
// design tokens (the logs view renders inside `.campaigns-rd`).
const StatChip = ({
    label,
    value,
    color,
    isActive,
    onClick,
    tooltip,
}: {
    label: string;
    value: number;
    color: string;
    isActive: boolean;
    onClick: () => void;
    tooltip?: string;
}) => {
    const chip = (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 12px',
                borderRadius: 999,
                border: `1px solid ${isActive ? color : 'var(--line)'}`,
                background: isActive ? `${color}14` /* 8% tint */ : 'var(--surface)',
                boxShadow: isActive ? `inset 0 0 0 1px ${color}` : undefined,
                cursor: 'pointer',
                transition: 'background .15s, border-color .15s',
                font: 'inherit',
                lineHeight: 1,
            }}
        >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span className="num" style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
                {value.toLocaleString()}
            </span>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>{label}</span>
            {tooltip && <Info style={{ width: 12, height: 12, color: 'var(--muted-2)', flexShrink: 0 }} />}
        </button>
    );

    if (tooltip) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{chip}</TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                        <p className="text-sm">{tooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
    return chip;
};

// Shared status palette — single source of truth (campaign-status-colors), so the logs
// tiles read as one system with the campaign page and top-bar badge.
const STATUS_PALETTE = CAMPAIGN_STATUS_COLORS;

export const CampaignMessageLogStatusFilter = ({ messageStats, activeFilter, onFilterClick, notOnWhatsAppCount = 0 }: CampaignMessageLogStatusFilterProps) => {
    const { t } = useTranslation(namespace);
    // Expired messages count toward failed (they won't be retried automatically)
    // Note: messageStats.expired is already calculated separately in the selector,
    // and messageStats.sent already excludes expired messages, so no subtraction needed
    const expiredCount = messageStats.expired || 0;
    const totalFailed = messageStats.failed + expiredCount;

    const stats = [
        {
            label: t('campaignLogs.status.sent'),
            // "Sent" here = reached the wire but no delivery receipt yet: primary
            // 'sent' + 'confirmed' (server-acked, receipt pending). `confirmed` was
            // split out of `sent`, so it is added back to keep this tab's count whole
            // (there is no separate confirmed tab in this legacy filter). Already
            // excludes expired (calculated in the selector).
            value: messageStats.sent + (messageStats.confirmed ?? 0),
            color: STATUS_PALETTE.sending,
            filter: 'bt-sent' as BulkLogsViewFilterType,
            tooltip: t('campaignLogs.tooltips.sending'),
        },
        {
            label: t('campaignLogs.status.delivered'),
            // The stats shape exposes the delivery-receipt bucket as `delivered`
            // (green ✓✓); reading the non-existent `received` returned undefined and
            // crashed the whole logs view on `.toLocaleString()`.
            value: messageStats.delivered,
            color: STATUS_PALETTE.delivered,
            filter: 'received' as BulkLogsViewFilterType,
        },
        {
            label: t('campaignLogs.status.read'),
            value: messageStats.read,
            color: STATUS_PALETTE.read,
            filter: 'read' as BulkLogsViewFilterType,
        },
        {
            label: t('campaignLogs.status.interacted'),
            value: messageStats.played,
            color: STATUS_PALETTE.interacted,
            filter: 'played' as BulkLogsViewFilterType,
            tooltip: t('campaignLogs.tooltips.interacted'),
        },
        {
            label: t('campaignLogs.status.failed'),
            value: totalFailed,
            color: STATUS_PALETTE.failed,
            filter: 'failed' as BulkLogsViewFilterType,
            tooltip: expiredCount > 0 ? t('campaignLogs.tooltips.failedWithExpired', { count: expiredCount }) : undefined,
        },
        // BE#710 — a sub-slice of failed: recipients not registered on WhatsApp. Only when present.
        ...(notOnWhatsAppCount > 0 ? [{
            label: t('campaignLogs.filter.notOnWhatsApp'),
            value: notOnWhatsAppCount,
            color: '#f59e0b',
            filter: 'not_on_whatsapp' as BulkLogsViewFilterType,
            tooltip: undefined,
        }] : []),
    ];

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {stats.map((stat) => (
                <StatChip
                    key={stat.filter}
                    label={stat.label}
                    value={stat.value}
                    color={stat.color}
                    isActive={activeFilter === stat.filter}
                    onClick={() => onFilterClick(stat.filter)}
                    tooltip={stat.tooltip}
                />
            ))}
        </div>
    );
};

export default CampaignMessageLogStatusFilter;
